import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MessageModal from '@/components/feature/MessageModal';
import RatingModal from '@/components/feature/RatingModal';
import { useMessages } from '@/hooks/useMessages';
import { fieldLabels, formatFieldValue, type GreenCertField } from '@/components/feature/GreenCertFieldConfig';
import { supplyApi, demandApi, type SupplyItem, type DemandItem } from '@/services/api';

const DETAIL_FIELDS: GreenCertField[] = [
  'direction',
  'year',
  'halfYear',
  'month',
  'projectType',
  'channel',
  'provinceRequirement',
  'subsidy',
  'tradeMethod',
  'exchange',
  'offerBy',
  'tradePrinciple',
];

export default function DetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const itemId = Number(id);

  const [item, setItem] = useState<SupplyItem | DemandItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const { sendMessage } = useMessages();

  useEffect(() => {
    if (!type || !id) return;
    setLoading(true);
    setError(null);

    const apiCall = type === 'supply' ? supplyApi.detail(itemId) : demandApi.detail(itemId);

    apiCall
      .then((data) => {
        setItem(data);
        setLoading(false);
      })
      .catch(() => {
        setError('信息不存在或已被删除');
        setLoading(false);
      });
  }, [type, id, itemId]);

  if (loading) {
    return (
      <div className="w-full bg-background-50 min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <>
        <Navbar />
        <main className="w-full bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">{error || '信息不存在或已被删除'}</p>
            <Link to="/" className="inline-block mt-3 text-primary-600 text-sm hover:underline cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isSupply = type === 'supply';
  const certTypeLabel = formatFieldValue('projectType', item.projectType);

  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50 min-h-screen">
        {/* Header */}
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-2 text-sm text-foreground-400 mb-4">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/${type}`} className="hover:text-foreground-600 transition-colors cursor-pointer">
                {isSupply ? '绿证供给' : '采购需求'}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{item.company}</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {item.company}
              </h1>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${
                isSupply ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
              }`}>
                {certTypeLabel}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line text-xs"></i>
                {item.region}
              </span>
              <span className="text-foreground-400 text-xs">|</span>
              <span>发布于 {item.publishDate}</span>
              <span className="text-foreground-400 text-xs">|</span>
              {item.status === 'active' ? (
                <span className="text-primary-600 font-medium">
                  {isSupply ? '挂牌中' : '征集中'}
                </span>
              ) : (
                <span className="text-foreground-400">已过期</span>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Key stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">{isSupply ? '绿证数量' : '采购数量'}</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    {item.quantity.toLocaleString()}
                  </p>
                  <p className="text-foreground-400 text-xs">个</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">{isSupply ? '参考报价' : '预算价格'}</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    &yen;{item.price}
                  </p>
                  <p className="text-foreground-400 text-xs">元/个</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">有效期至</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    {item.expiryDate}
                  </p>
                  <p className="text-foreground-400 text-xs">
                    {item.status === 'active' ? (
                      <span className="text-primary-600">{isSupply ? '挂牌中' : '征集中'}</span>
                    ) : (
                      <span className="text-foreground-400">已过期</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Transaction details */}
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  {isSupply ? '交易详情' : '采购要求'}
                </h3>
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {DETAIL_FIELDS.map((field) => {
                      const raw = item[field as keyof typeof item];
                      if (raw === undefined || raw === null || (Array.isArray(raw) && raw.length === 0)) return null;
                      const val = formatFieldValue(field, raw as string | string[]);
                      return (
                        <div key={field} className="flex items-start gap-3">
                          <span className="text-foreground-400 text-xs flex-shrink-0 w-18">
                            {fieldLabels[field]}
                          </span>
                          <span className="text-foreground-800 text-sm">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                  详细说明
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-line">
                  {item.description}
                </p>
              </div>

              {/* Other info */}
              {item.otherInfo && (
                <div className="mb-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                    其他信息
                  </h3>
                  <div className="bg-background-100 rounded-lg p-4">
                    <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-line">
                      {item.otherInfo}
                    </p>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="border border-accent-200 bg-accent-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-sm font-medium">交易风险提示</p>
                    <p className="text-accent-700 text-xs mt-1 leading-relaxed">
                      本平台为纯免费信息撮合平台，不参与交易、资金结算及权属过户。信息真实性由发布方负责，请务必自行核验对方资质与绿证真伪后再进行交易。如发现虚假信息，请及时举报。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-background-100 rounded-lg p-6 sticky top-24">
                <div className="text-center mb-5">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                    isSupply ? 'bg-primary-100' : 'bg-accent-100'
                  }`}>
                    <i className={`text-2xl ${
                      isSupply ? 'ri-building-4-line text-primary-600' : 'ri-building-2-line text-accent-600'
                    }`}></i>
                  </div>
                  <h4 className="font-heading text-base font-bold text-foreground-900 mt-3">
                    {item.company}
                  </h4>
                  <p className="text-foreground-400 text-xs mt-1">
                    {item.region} · {isSupply ? '供给方' : '需求方'}
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-400">联系人</span>
                    <span className="text-foreground-700">{item.contactPerson}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-400">联系电话</span>
                    <span className="text-foreground-700">{item.contactPhone}</span>
                  </div>
                </div>
                <p className="text-foreground-400 text-xs italic text-center">
                  * 请登录后查看完整联系信息
                </p>

                <div className="mt-4 pt-4 border-t border-background-200">
                  <button
                    onClick={() => setMessageOpen(true)}
                    className="w-full py-2.5 border border-primary-400 text-primary-600 text-sm font-medium rounded-md hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line mr-1.5"></i>
                    发站内信
                  </button>
                  {item.userId != null && (
                    <button
                      onClick={() => setRateOpen(true)}
                      className="w-full mt-2 py-2.5 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-star-line mr-1.5"></i>
                      评价 TA
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <MessageModal
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        listingId={item.id}
        listingType={type as 'supply' | 'demand'}
        listingTitle={`${item.company} - ${certTypeLabel}`}
        receiverName={item.company}
        receiverId={item.userId ?? 0}
        onSend={(content) => {
          if (!item.userId) return;
          sendMessage(item.userId, {
            listingId: item.id,
            listingType: type as 'supply' | 'demand',
            listingTitle: `${item.company} - ${certTypeLabel}`,
            receiverId: item.userId,
            receiverName: item.company,
            content,
          });
        }}
      />
      {item.userId != null && (
        <RatingModal
          open={rateOpen}
          onClose={() => setRateOpen(false)}
          rateeId={item.userId}
          listingType={type as 'supply' | 'demand'}
          listingId={item.id}
        />
      )}
    </>
  );
}